// Avery 5163 label sheet generator — ported verbatim from the mobile app
// for pixel-perfect parity with the on-floor printer.
// 2" × 4" labels, 10 per sheet (2 cols × 5 rows) on US Letter.

import { resolveItemColor } from "@/lib/colorSwatch";

const BACKEND_URL_ENV = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/$/, "");
const FALLBACK_BACKEND = "https://picking-building-qa.emergent.host";

function resolveBackend() {
  // Honour env only for local dev (localhost). Otherwise always use the real backend.
  if (BACKEND_URL_ENV && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(BACKEND_URL_ENV)) {
    return BACKEND_URL_ENV;
  }
  return FALLBACK_BACKEND;
}

// Logo is bundled in /public — resolve to an absolute URL the iframe can fetch.
const LABEL_LOGO_FALLBACK_URL = typeof window !== "undefined"
  ? `${window.location.origin}${process.env.PUBLIC_URL || ""}/cd-logo.png`
  : `${resolveBackend()}/cd-logo.png`;

export function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

const BARCODE_PREFIX = "CD:item:";

// Encode each label as `CD:item:{item_uuid}|o:{order_uuid}` so the scanner can
// detect labels physically applied to the wrong cabinet — backend returns 409
// "Label mismatch" when the embedded order context doesn't match the item's
// actual order, and the mobile app surfaces a "Wrong order" warning.
//
// - Item ref: local per-item UUID (`item.id`), unique per item-instance.
//   external_id is used only when `id` is missing (defensive).
// - Order ref: local order UUID, threaded in from the sheet-level `orderId`.
//   If absent (e.g. legacy callers), we emit the legacy `CD:item:{ref}` form
//   which the backend continues to accept.
//
// Unknown extension keys are silently ignored by the backend parser, so this
// format is forward-compatible (e.g. `|u:<user>|t:<ts>` could be added later).
function barcodeFor(item, orderId) {
  const itemRef = (item.id && String(item.id).trim()) || item.external_id;
  const orderRef = item.order_id || orderId || "";
  if (!orderRef) return `${BARCODE_PREFIX}${itemRef}`;
  return `${BARCODE_PREFIX}${itemRef}|o:${orderRef}`;
}

const TRIM_KEYWORDS = [
  "trim", "skin", "filler", "molding", "moulding", "crown",
  "scribe", "corner", "toe kick", "toekick", "toe-kick",
  "baseboard", "shoe", "valance", "panel",
];

export function isTrimCategory(category) {
  const c = (category || "").toLowerCase();
  if (!c) return false;
  return TRIM_KEYWORDS.some((kw) => c.includes(kw));
}

/**
 * Trim items → 1 label with QTY badge.
 * Non-trim items with qty > 1 → N identical labels (one per physical cabinet).
 */
export function expandLabelItems(items) {
  const out = [];
  for (const it of items) {
    const total = Math.max(1, it.units_total || 1);
    const trim = isTrimCategory(it.category);
    if (total <= 1 || trim) {
      out.push({ ...it });
    } else {
      for (let i = 0; i < total; i++) out.push({ ...it });
    }
  }
  return out;
}

function renderColorLine(item) {
  // Base44 contract (2026-06): the `color` field is source of truth, with the
  // per-item `custom_paint_name/code/hex` fields taking priority when present.
  // resolveItemColor() encapsulates the full precedence.
  const info = resolveItemColor(item);
  const text = info.text;
  const hex = info.hex;

  if (!text && !hex) return `<div class="color">—</div>`;

  // Custom paint / spec reference — verbatim text + amber "CUSTOM" pill so
  // the painter knows this is not a stock finish. Swatch only when we have
  // an explicit hex from custom_paint_hex.
  if (info.isCustom) {
    const swatch = hex
      ? `<span class="colorSwatch" style="background:${hex};" data-testid="label-color-swatch"></span>`
      : "";
    return `
      <div class="color colorCustom">
        ${swatch}<span class="colorText">${escapeHtml(text)}</span>
        <span class="customPill" data-testid="label-color-custom">CUSTOM</span>
      </div>`;
  }

  if (!hex) return `<div class="color"><span class="colorText">${escapeHtml(text)}</span></div>`;

  // The .colorSwatch class always carries a 0.75pt black border so the
  // swatch stays visible when printed on a B&W copier (especially for white
  // and light colors that would otherwise vanish into the paper).
  return `
    <div class="color">
      <span class="colorSwatch" style="background:${hex};" data-testid="label-color-swatch"></span>
      <span class="colorText">${escapeHtml(text || "")}</span>
    </div>`;
}

/**
 * Detect left/right handing from an item's modification text.
 * Looks for "Door L" / "Door R" (and a couple of common variants) and returns
 * "L", "R", or null. Case-insensitive, tolerates extra whitespace.
 *
 * Recognized:
 *   • "Door L" / "Door R"
 *   • "Door Left" / "Door Right"
 *   • "L Door" / "R Door"
 *   • "LH" / "RH" appearing as a standalone token
 */
function extractHanding(item) {
  const mods = (item && item.modifications) || "";
  if (!mods) return null;
  const txt = String(mods);
  // Door L / Door R (with optional "eft"/"ight")
  let m = txt.match(/\bDoor\s+(L|R)(?:eft|ight)?\b/i);
  if (m) return m[1].toUpperCase();
  // L Door / R Door
  m = txt.match(/\b(L|R)(?:eft|ight)?\s+Door\b/i);
  if (m) return m[1].toUpperCase();
  // Standalone LH / RH token
  m = txt.match(/\b(L|R)H\b/i);
  if (m) return m[1].toUpperCase();
  return null;
}

export function pickOrderShippingAddress(order) {
  if (!order) return "";

  // 1. Canonical pre-formatted full address from the backend.
  const direct = order.shipping_address || order.delivery_address || order.ship_to_address || order.ship_to || "";
  if (typeof direct === "string" && direct.trim()) return direct.trim();

  // 2. Structured object (older Base44 payloads).
  if (direct && typeof direct === "object") {
    const parts = [
      direct.street1 || direct.line1 || direct.address1 || direct.street,
      direct.street2 || direct.line2 || direct.address2 || direct.unit,
      [direct.city, direct.state || direct.region, direct.zip || direct.postal_code]
        .filter(Boolean).join(" "),
    ].filter((p) => p && String(p).trim());
    if (parts.length) return parts.join(", ");
  }

  // 3. New canonical per-field shape (shipping_street + city/state/zip + unit_number).
  const street = (order.shipping_street || "").trim();
  if (street) {
    const unit = (order.unit_number || order.unit || order.suite || order.apt || "").trim();
    // Only append unit when it's not already embedded in the street line.
    const streetWithUnit = unit && !street.toLowerCase().includes(unit.toLowerCase())
      ? `${street}, ${unit}`
      : street;
    const csz = [
      order.shipping_city,
      [order.shipping_state, order.shipping_zip].filter(Boolean).join(" "),
    ].filter((p) => p && String(p).trim()).join(", ");
    // shipping_country is null for US by contract — only append when present.
    const country = (order.shipping_country || "").trim();
    return [streetWithUnit, csz, country].filter(Boolean).join(", ");
  }

  // 4. Legacy `customer_address` fallback (always populated per backend contract).
  const legacy = (order.customer_address || "").trim();
  if (legacy && legacy !== "—") return legacy;

  // 5. Last resort — bare unit only.
  const unitOnly = order.unit_number || order.unit || order.suite || order.apt;
  if (unitOnly && String(unitOnly).trim()) return `Unit ${String(unitOnly).trim()}`;
  return "";
}

/**
 * Render a customer name onto the label.
 *
 * Historically we truncated to the surname-only for individual customers
 * ("John Smith" → "Smith") to save horizontal space. That rule produced
 * false-positive truncations:
 *   • Business names collapsed to a single keyword ("Blacklough Construction"
 *     → "Construction").
 *   • Names with a numeric or ordinal disambiguation suffix collapsed to the
 *     suffix only ("Corey Friedrich 2" → "2").
 *
 * The `.customer` CSS now clamps to 2 lines at 19pt with word-break, so the
 * full name fits on the Avery 5163 cell without overflowing the QR / tag
 * column. We therefore render the customer name verbatim and let the layout
 * handle wrapping.
 *
 * `"Last, First"` payloads from legacy imports still get normalized to the
 * natural reading order so the surname shows first on the label.
 */
export function labelCustomerName(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  // Legacy "Last, First" → "First Last" so the display name reads naturally.
  // Only when there's exactly one comma and both sides are non-empty — leaves
  // multi-part strings ("Smith, John & Jane") untouched.
  if (s.includes(",")) {
    const parts = s.split(",").map((p) => p.trim()).filter(Boolean);
    if (parts.length === 2) return `${parts[1]} ${parts[0]}`.trim();
  }
  return s;
}

function labelCellHtml(item, logoSrc, orderNumber, orderId, store, shippingAddress) {
  const code = barcodeFor(item, orderId);
  // ecc=H gives ~30% error correction, enough that the center logo (~22%) doesn't break scanning.
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&margin=0&ecc=H&data=${encodeURIComponent(code)}`;
  const hasMod = !!(item.modifications && item.modifications.trim());
  const total = item.units_total || 1;
  const trim = isTrimCategory(item.category);
  const showQty = trim && total > 1;
  // Pull from the item first (most accurate), fall back to the sheet's order number.
  const orderRef = item.order_number || orderNumber || "";
  // Same priority for store: per-item override (if backend ever populates it),
  // else the sheet-level order.store.
  const storeRef = item.store || store || "";
  const hand = extractHanding(item);
  const cellClass = `cell${showQty ? " cellWithQty" : ""}${storeRef ? " cellWithStore" : ""}${hasMod ? " cellWithMod" : ""}`;
  return `
    <div class="${cellClass}">
      <div class="qrBox">
        <img class="qrImg" src="${qr}" />
        <div class="qrLogoWrap"><img class="qrLogo" src="${logoSrc}" /></div>
      </div>
      ${orderRef ? `<div class="orderTag" data-testid="label-order-tag">${escapeHtml(orderRef)}</div>` : ""}
      ${storeRef ? `<div class="storeTag" data-testid="label-store-tag">${escapeHtml(storeRef)}</div>` : ""}
      ${showQty ? `<div class="qtyBadge">QTY: ${total}</div>` : ""}
      <div class="topBlock">
        <div class="customer">${escapeHtml(labelCustomerName(item.customer_name) || "—")}</div>
        ${shippingAddress ? `<div class="address" data-testid="label-address">${escapeHtml(shippingAddress)}</div>` : ""}
        ${renderColorLine(item)}
        <div class="sku">${escapeHtml(item.sku)}${hand ? `<span class="handBadge" data-testid="label-hand-${hand}">-${hand}</span>` : ""}</div>
        <div class="name">${escapeHtml(item.name)}</div>
      </div>
      ${hasMod ? `
        <div class="mod">
          <span class="modStar">★</span>
          <span class="modLbl">MOD:</span>
          <span class="modText">${escapeHtml(item.modifications.trim())}</span>
        </div>` : ""}
    </div>`;
}

function chunks(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

const SHEET_CSS = `
@page { size: letter; margin: 0; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; background: #fff; font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #000; }

.sheet {
  width: 8.5in;
  height: 11in;
  padding: 0.5in 0.156in;
  display: grid;
  grid-template-columns: 4in 4in;
  grid-template-rows: repeat(5, 2in);
  column-gap: 0.188in;
  row-gap: 0;
  page-break-after: always;
  position: relative;
}
.sheet:last-child { page-break-after: auto; }

/* Sheet footer watermark — sits in the bottom 0.5" margin area below the labels.
   Small, monochrome, traceable on a reprint. Stays under all label cells. */
.sheetFooter {
  position: absolute;
  left: 0.156in;
  right: 0.156in;
  bottom: 0.18in;
  font-size: 6pt;
  letter-spacing: 0.5px;
  color: #6b7280;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: "Courier New", monospace;
  text-transform: uppercase;
}
.sheetFooter .fLogo {
  display: inline-block;
  width: 8pt; height: 8pt;
  vertical-align: middle;
  margin-right: 4pt;
}
.sheetFooter .fBrand { font-weight: 800; color: #C8102E; letter-spacing: 1px; }

.cell {
  width: 4in;
  height: 2in;
  padding: 0.10in 0.14in;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}
.cell.empty { background: transparent; }

.topBlock { flex: 1 1 auto; min-height: 0; overflow: hidden; }

.qrBox {
  position: absolute;
  right: 0.10in; top: 0.10in;
  width: 0.85in; height: 0.85in;
  padding: 2px;
  border: 1px solid #000;
  background: #fff;
}
.qrBox .qrImg { width: 100%; height: 100%; display: block; }
.qrLogoWrap {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 0.26in; height: 0.26in;
  background: #fff;
  border-radius: 2px;
  padding: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.qrLogo { width: 100%; height: 100%; display: block; object-fit: contain; }

.qtyBadge {
  position: absolute;
  left: 0.10in; top: 0.10in;
  /* Black bg prints darker than red on B&W copiers — keeps QTY readable. */
  background: #000;
  color: #fff;
  font-size: 18pt;
  font-weight: 900;
  padding: 3pt 8pt;
  border-radius: 4pt;
  letter-spacing: 0.6px;
  line-height: 1.0;
  border: 1.25pt solid #000;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
/* When a QTY badge is present, shift the top text block right so the badge
   doesn't overlap the customer/color/sku/name. Width: badge ~0.80in plus
   a 4pt gap. The right side already clears the QR via padding-right.
   We rely on an explicit cellWithQty class rather than :has(.qtyBadge)
   because some print engines don't support :has() in print stylesheets. */
.cell.cellWithQty .topBlock {
  padding-left: 0.85in;
}

.orderTag {
  position: absolute;
  right: 0.10in;
  top: calc(0.10in + 0.85in + 2pt);
  /* Default narrow (matches QR); widens when a store tag is also present. */
  width: 0.85in;
  text-align: center;
  /* Switched from black-fill to outlined pill — printers / Chrome "Background
     graphics: off" were dropping the fill, leaving faint gray text. Bold
     black border + bold black text reliably renders on any printer. */
  background: #fff;
  color: #000;
  border: 1.25pt solid #000;
  font-family: "Courier New", monospace;
  font-size: 9pt;
  font-weight: 900;
  letter-spacing: 0.3px;
  line-height: 1.0;
  padding: 2pt 0;
  border-radius: 2pt;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
/* When a store tag is also present, widen the order tag to match and bump
   its font so they read as a stacked pair. Handled via explicit className
   .cell.cellWithStore .orderTag below for print-engine compatibility. */

.storeTag {
  position: absolute;
  right: 0.10in;
  /* Sit below the orderTag (which is at top + 0.85in QR + 2pt + ~11pt pill height + 2pt gap). */
  top: calc(0.10in + 0.85in + 2pt + 12pt + 4pt);
  /* Wider than the QR width — extends further left into the card so longer
     store names fit at a readable font size. */
  width: 1.40in;
  max-width: calc(100% - 0.20in);
  text-align: center;
  background: #fff;
  color: #000;
  border: 1.25pt solid #000;
  font-family: "Courier New", monospace;
  font-size: 11pt;
  font-weight: 900;
  letter-spacing: 0.5px;
  line-height: 1.0;
  padding: 3pt 4pt;
  border-radius: 3pt;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.customer {
  font-size: 19pt;
  font-weight: 900;
  line-height: 1.0;
  letter-spacing: -0.5px;
  padding-right: 0.95in;
  padding-top: 0.10in;
  /* Business names are usually 2-4 words and don't fit on a single 19pt line
     — allow up to 2 lines, ellipsis if still too long, and let the browser
     hyphenate/wrap on whitespace so we never end up ambiguously showing just
     the trailing word (the old "just says Construction" bug). */
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  word-break: break-word;
}
/* When the customer name is a single short surname (a "last-name-only" style
   render), the top row still uses the big 19pt / 1-line look — enforced by
   inserting a modifier class only when name-length > 18 chars. We keep this
   simple by relying on the 2-line clamp above; short names still occupy a
   single line naturally. */
.address {
  font-size: 10pt;
  font-weight: 800;
  line-height: 1.1;
  margin-top: 1pt;
  padding-right: 0.95in;
  color: #000;
  /* Allow up to 2 lines so a full street + city/state/zip can both show. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* When a wider storeTag is present, give the left-column text more right
   padding so it doesn't run under the tag stack. The default 0.95in clears
   the QR (0.85in + gap); 1.45in clears the storeTag (1.40in + gap). */
.cell.cellWithStore .customer,
.cell.cellWithStore .address,
.cell.cellWithStore .color,
.cell.cellWithStore .sku,
.cell.cellWithStore .name {
  padding-right: 1.45in;
}
/* And the order tag widens to match the storeTag, with bigger font. */
.cell.cellWithStore .orderTag {
  width: 1.40in;
  font-size: 10pt;
  padding: 3pt 0;
}

.color {
  font-size: 18pt;
  font-weight: 800;
  line-height: 1.0;
  margin-top: 3pt;
  padding-right: 0.95in;
  /* Black for max B&W printer contrast. The swatch carries the color cue. */
  color: #000;
  display: flex;
  align-items: center;
  gap: 5pt;
  min-width: 0;
}
.color > .colorText {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1 1 auto;
}
/* Legacy selector (unqualified last-span) — kept for any label callers that
   haven't been migrated yet. */
.color > span:last-child:not(.customPill):not(.colorText) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
/* Custom paint / spec reference — verbatim string can be very long
   ("SW 7008 Alabaster - Custom Match per drawing #4501B"). Drop the base
   font size so more of the string fits on the sticker before ellipsis. */
.color.colorCustom {
  font-size: 12pt;
  line-height: 1.15;
  /* Allow the text to wrap up to 2 lines instead of ellipsing on 1, so a
     full SW / BM spec name is readable on the physical label. */
  align-items: flex-start;
}
.color.colorCustom > .colorText {
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}
.customPill {
  flex: 0 0 auto;
  display: inline-block;
  /* Black bg + white text for max B&W-printer contrast (amber prints
     invisibly on a monochrome copier). */
  background: #000;
  color: #fff;
  border: 1pt solid #000;
  font-family: "Courier New", monospace;
  font-size: 8pt;
  font-weight: 900;
  letter-spacing: 0.5px;
  padding: 1pt 4pt;
  border-radius: 2pt;
  line-height: 1.0;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.colorSwatch {
  display: inline-block;
  flex-shrink: 0;
  width: 17pt;
  height: 17pt;
  border-radius: 50%;
  /* Thick border so the swatch stays visible even when the fill is white /
     light and the printer drops background colors. */
  border: 1.5pt solid #000;
  /* Inner glyph helps identify the swatch even on B&W copies with very
     light fills (added via box-shadow inset so it doesn't disrupt layout). */
  box-shadow: inset 0 0 0 1pt #fff, inset 0 0 0 2pt #000;
  /* Honors background set inline. */
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.sku {
  font-family: "Courier New", monospace;
  font-size: 13pt;
  font-weight: 800;
  line-height: 1.0;
  margin-top: 5pt;
  letter-spacing: 0.5px;
  padding-right: 0.95in;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.handBadge {
  display: inline-block;
  margin-left: 4pt;
  padding: 1pt 4pt;
  /* Black for max contrast on B&W printers (red prints as light gray). */
  background: #000;
  color: #fff;
  border-radius: 3pt;
  font-size: 12pt;
  font-weight: 900;
  letter-spacing: 0.5px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.name {
  font-size: 11pt;
  font-weight: 700;
  line-height: 1.2;
  margin-top: 2pt;
  padding-right: 0.95in;
  /* Clean 2-line clamp so long descriptions never slice through letters. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* When a MOD strip is present the cell is tighter — clamp the name to a single
   line so the yellow MOD block can sit cleanly below it without overflow. */
.cell.cellWithMod .name {
  -webkit-line-clamp: 1;
}
.cell.cellWithMod .mod .modText {
  /* Keep extremely long mod text from running off the bottom of the cell. */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mod {
  flex: 0 0 auto;
  margin-top: 4pt;
  /* Yellow prints almost invisibly on B&W copiers — switched to white bg
     with a thick black border and inverted MOD: pill so the strip stays
     readable in any print path. */
  background: #fff;
  border: 1.5pt solid #000;
  padding: 4pt 6pt 4pt 4pt;
  display: flex;
  align-items: flex-start;
  gap: 4pt;
  font-size: 10pt;
  font-weight: 800;
  line-height: 1.2;
  color: #000;
}
.modStar {
  font-size: 12pt;
  line-height: 1.0;
  flex: 0 0 auto;
  color: #000;
}
.modLbl {
  letter-spacing: 0.5px;
  flex: 0 0 auto;
  /* Inverted pill — high contrast even when printed monochrome. */
  background: #000;
  color: #fff;
  padding: 1pt 4pt;
  border-radius: 2pt;
  font-weight: 900;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.modText {
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: anywhere;
  white-space: normal;
}
`;

/**
 * Build an HTML document that prints onto Avery 5163 (10 per sheet).
 * `startOffset` (0–9) leaves blank cells at the top so a partially-used sheet
 * can be reused. A small footer watermark is added inside the 0.5" bottom
 * margin so every reprint is traceable to the source order + timestamp.
 */
export function avery5163SheetHtml(items, { orderNumber, orderId, store, shippingAddress, startOffset = 0, printedBy, logoDataUri } = {}) {
  const logoSrc = logoDataUri || LABEL_LOGO_FALLBACK_URL;
  const expanded = expandLabelItems(items);
  const cells = expanded.map((it) => labelCellHtml(it, logoSrc, orderNumber, orderId, store, shippingAddress));
  const offset = Math.max(0, Math.min(9, startOffset | 0));
  const withOffset = offset
    ? Array(offset).fill('<div class="cell empty"></div>').concat(cells)
    : cells;
  const padded = withOffset.length % 10 === 0
    ? withOffset
    : withOffset.concat(Array((10 - (withOffset.length % 10)) % 10).fill('<div class="cell empty"></div>'));

  const sheetGroups = chunks(padded, 10);
  const total = sheetGroups.length;
  const now = new Date();
  const stamp = now.toISOString().replace("T", " ").slice(0, 16) + " UTC";
  const byBit = printedBy ? ` · ${escapeHtml(printedBy)}` : "";
  const orderBit = orderNumber ? ` · ${escapeHtml(orderNumber)}` : "";

  const pages = sheetGroups.map((page, i) => {
    const footer = `
      <div class="sheetFooter">
        <div>
          <img class="fLogo" src="${logoSrc}" />
          <span class="fBrand">Cabinet Depot</span>
          <span>${orderBit ? orderBit.slice(3) : ""}</span>
        </div>
        <div>${escapeHtml(stamp)}${byBit}</div>
        <div>Sheet ${i + 1} / ${total}</div>
      </div>`;
    return `<section class="sheet">${page.join("")}${footer}</section>`;
  }).join("");

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Cabinet Depot Labels${orderNumber ? " — " + escapeHtml(orderNumber) : ""}</title>
    <style>${SHEET_CSS}</style>
  </head>
  <body>${pages}</body>
</html>`;
}

/**
 * Trigger an isolated print of the given HTML using a hidden iframe so the host
 * page (sidebar, banner) doesn't appear in the print preview.
 */
export function printAvery5163Web(html) {
  return new Promise((resolve, reject) => {
    try {
      const existing = document.getElementById("__cd_print_frame__");
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

      const frame = document.createElement("iframe");
      frame.id = "__cd_print_frame__";
      frame.setAttribute("aria-hidden", "true");
      frame.setAttribute("tabindex", "-1");
      frame.style.position = "fixed";
      frame.style.right = "0";
      frame.style.bottom = "0";
      frame.style.width = "0";
      frame.style.height = "0";
      frame.style.border = "0";
      frame.style.opacity = "0";
      frame.style.pointerEvents = "none";
      frame.srcdoc = html;

      let printed = false;
      const cleanup = () => {
        try { if (frame && frame.parentNode) frame.parentNode.removeChild(frame); }
        catch (err) { console.warn("Print frame cleanup failed:", err); }
      };

      const triggerPrint = () => {
        if (printed) return;
        printed = true;
        const cw = frame.contentWindow;
        if (!cw) { cleanup(); reject(new Error("Print frame has no contentWindow")); return; }
        // Brief delay so QR images + logo finish loading before snapshot
        setTimeout(() => {
          try {
            cw.focus();
            cw.print();
            setTimeout(() => { cleanup(); resolve(); }, 1500);
          } catch (e) {
            console.warn("Print invocation failed:", e);
            cleanup();
            reject(e);
          }
        }, 600);
      };

      frame.onload = triggerPrint;
      setTimeout(triggerPrint, 2500);
      document.body.appendChild(frame);
    } catch (e) {
      reject(e);
    }
  });
}
